import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { config } from '../config';
import { NotificationSettingsPayload, NotificationSettingsResponse, PollNowResponse, NotificationHistoryItem, NotificationListResponse, User, AppCatalogItem } from '../types';

const API_BASE_URL = config.getApiUrl();

console.log('API Base URL:', API_BASE_URL);

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Добавляем токен к запросам
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Обрабатываем ошибки
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints

  // Telegram Login verification
  async telegramVerify(payload: {
    id: number;
    auth_date: number;
    hash: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
  }) {
    const response: AxiosResponse = await this.api.post('auth/telegram/verify', payload);
    return response.data;
  }

  async telegramWebAppVerify(initData: string) {
    const response: AxiosResponse = await this.api.post('auth/telegram/webapp-verify', { init_data: initData });
    return response.data;
  }

  // Удалено: регистрация/логин по паролю не поддерживаются

  async getCurrentUser() {
    const response: AxiosResponse = await this.api.get('auth/me');
    return response.data;
  }

  // Device endpoints
  async getDevices() {
    const response: AxiosResponse = await this.api.get('devices/');
    return response.data;
  }

  async checkCardUsage(cardNumber: string, deviceId?: number) {
    const params = deviceId ? `?device_id=${deviceId}` : '';
    const response: AxiosResponse = await this.api.get(`devices/check-card/${cardNumber}${params}`);
    return response.data;
  }

  async createDevice(device: any) {
    const response: AxiosResponse = await this.api.post('devices/', device);
    return response.data;
  }

  async updateDevice(id: number, device: any) {
    const response: AxiosResponse = await this.api.put(`devices/${id}`, device);
    return response.data;
  }

  async deleteDevice(id: number) {
    const response: AxiosResponse = await this.api.delete(`devices/${id}`);
    return response.data;
  }

  async withdrawFromDevice(id: number, amount: number) {
    const response: AxiosResponse = await this.api.post(`devices/${id}/withdraw`, {
      amount,
    });
    return response.data;
  }

  async updateAllBalances() {
    const tz_offset = new Date().getTimezoneOffset();
    const response: AxiosResponse = await this.api.post('devices/update-balances', { tz_offset });
    return response.data;
  }

  // Statistics endpoints
  async getDailyStatistics(days: number = 30) {
    const response: AxiosResponse = await this.api.get(`statistics/daily?days=${days}`);
    return response.data;
  }

  async getDailyStatisticsWithComparison(days: number = 30) {
    const response: AxiosResponse = await this.api.get(`statistics/daily-with-comparison?days=${days}`);
    return response.data;
  }

  async getDailyStatisticsByRange(startDate: Date, endDate: Date) {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const format = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const start = format(startDate);
    const end = format(endDate);
    const response: AxiosResponse = await this.api.get(`statistics/daily-with-comparison?start=${start}&end=${end}`);
    return response.data;
  }

  async getStatisticsOverview() {
    const response: AxiosResponse = await this.api.get('statistics/overview');
    return response.data;
  }

  async getMonthlyStatistics(year?: number, month?: number) {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const response: AxiosResponse = await this.api.get(`statistics/monthly?${params}`);
    return response.data;
  }

  async getYearlyStatistics(year?: number) {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    const response: AxiosResponse = await this.api.get(`statistics/yearly?${params}`);
    return response.data;
  }

  async reorderDevices(orderData: {id: number, order: number}[]) {
    const response: AxiosResponse = await this.api.post('devices/reorder', orderData);
    return response.data;
  }

  async getMonthlyLeaders(top_n: number = 50) {
    const response: AxiosResponse = await this.api.get(`statistics/leaders?top_n=${top_n}`);
    return response.data;
  }

  // Notifications
  async getNotificationSettings(): Promise<NotificationSettingsResponse> {
    const response: AxiosResponse = await this.api.get('notifications/settings');
    return response.data;
  }

  async updateNotificationSettings(payload: NotificationSettingsPayload): Promise<NotificationSettingsResponse> {
    const response: AxiosResponse = await this.api.put('notifications/settings', payload);
    return response.data;
  }

  async pollNow(): Promise<PollNowResponse> {
    const response: AxiosResponse = await this.api.get('notifications/poll-now');
    return response.data;
  }

  async telegramTestMessage(): Promise<{ ok: boolean }>{
    const response: AxiosResponse = await this.api.post('notifications/telegram-test');
    return response.data;
  }

  // Server notifications history
  async recordServerNotification(item: NotificationHistoryItem): Promise<NotificationHistoryItem> {
    const response: AxiosResponse = await this.api.post('notifications/record', item);
    return response.data;
  }

  async recordServerNotificationsBulk(items: NotificationHistoryItem[]): Promise<NotificationHistoryItem[]> {
    const response: AxiosResponse = await this.api.post('notifications/record-bulk', items);
    return response.data;
  }

  async getServerNotificationHistory(limit: number = 1000): Promise<NotificationListResponse> {
    const response: AxiosResponse = await this.api.get(`notifications/history?limit=${limit}`);
    return response.data;
  }

  async markServerNotificationsRead(ids: string[]): Promise<{ updated: number }>{
    const response: AxiosResponse = await this.api.post('notifications/mark-read', ids);
    return response.data;
  }

  async clearServerNotificationHistory(): Promise<{ deleted: number }>{
    const response: AxiosResponse = await this.api.delete('notifications/clear');
    return response.data;
  }

  // Admin endpoints
  async adminListUsers(q?: string): Promise<User[]> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    const response: AxiosResponse = await this.api.get(`admin/users${qs}`);
    return response.data;
  }

  async adminAllowUser(userId: number): Promise<User> {
    const response: AxiosResponse = await this.api.post(`admin/users/${userId}/allow`);
    return response.data;
  }

  async adminBlockUser(userId: number): Promise<User> {
    const response: AxiosResponse = await this.api.post(`admin/users/${userId}/block`);
    return response.data;
  }

  async adminDeleteUser(userId: number): Promise<{ deleted: boolean; user_id: number }>{
    const response: AxiosResponse = await this.api.delete(`admin/users/${userId}`);
    return response.data;
  }

  // Apps (для настроек/флагов)
  async listAppsEnabled(): Promise<AppCatalogItem[]> {
    const response: AxiosResponse = await this.api.get('apps');
    return response.data;
  }

  async listAppsAll(): Promise<AppCatalogItem[]> {
    const response: AxiosResponse = await this.api.get('apps/all');
    return response.data;
  }

  async getAppsFlagsMap(): Promise<Record<string, string>> {
    const response: AxiosResponse = await this.api.get('apps/flags');
    return response.data;
  }

  // Admin apps catalog
  async adminListApps(q?: string): Promise<AppCatalogItem[]> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    const response: AxiosResponse = await this.api.get(`admin/apps${qs}`);
    return response.data;
  }

  async adminCreateApp(payload: { app_id: string; country_code?: string; label?: string; enabled: boolean }): Promise<AppCatalogItem> {
    const response: AxiosResponse = await this.api.post('admin/apps', payload);
    return response.data;
  }

  async adminUpdateApp(appId: string, payload: { country_code?: string | null; label?: string | null; enabled?: boolean | null }): Promise<AppCatalogItem> {
    const response: AxiosResponse = await this.api.patch(`admin/apps/${encodeURIComponent(appId)}`, payload);
    return response.data;
  }

  async adminDeleteApp(appId: string): Promise<{ deleted: boolean; app_id: string }>{
    const response: AxiosResponse = await this.api.delete(`admin/apps/${encodeURIComponent(appId)}`);
    return response.data;
  }
}

export const apiService = new ApiService();

// Обёртки, чтобы не терялся контекст this
// Удалено: экспорт login/register
export const getCurrentUser = () => apiService.getCurrentUser();
export const getDevices = () => apiService.getDevices();
export const createDevice = (device: any) => apiService.createDevice(device);
export const updateDevice = (id: number, device: any) => apiService.updateDevice(id, device);
export const deleteDevice = (id: number) => apiService.deleteDevice(id);
export const withdrawFromDevice = (id: number, amount: number) => apiService.withdrawFromDevice(id, amount);
export const updateAllBalances = () => apiService.updateAllBalances();
export const getDailyStatistics = (days: number = 30) => apiService.getDailyStatistics(days);
export const getDailyStatisticsWithComparison = (days: number = 30) => apiService.getDailyStatisticsWithComparison(days);
export const getDailyStatisticsByRange = (startDate: Date, endDate: Date) => apiService.getDailyStatisticsByRange(startDate, endDate);
export const getStatisticsOverview = () => apiService.getStatisticsOverview();
export const getMonthlyStatistics = (year?: number, month?: number) => apiService.getMonthlyStatistics(year, month);
export const getYearlyStatistics = (year?: number) => apiService.getYearlyStatistics(year);
export const reorderDevices = (orderData: {id: number, order: number}[]) => apiService.reorderDevices(orderData);
export const checkCardUsage = (cardNumber: string, deviceId?: number) => apiService.checkCardUsage(cardNumber, deviceId);
export const getMonthlyLeaders = (top_n: number = 50) => apiService.getMonthlyLeaders(top_n);
export const getNotificationSettings = () => apiService.getNotificationSettings();
export const updateNotificationSettings = (payload: NotificationSettingsPayload) => apiService.updateNotificationSettings(payload);
export const pollNow = () => apiService.pollNow(); 
export const adminListUsers = (q?: string) => apiService.adminListUsers(q);
export const adminAllowUser = (userId: number) => apiService.adminAllowUser(userId);
export const adminBlockUser = (userId: number) => apiService.adminBlockUser(userId);
export const adminDeleteUser = (userId: number) => apiService.adminDeleteUser(userId);

export const listAppsEnabled = () => apiService.listAppsEnabled();
export const listAppsAll = () => apiService.listAppsAll();
export const getAppsFlagsMap = () => apiService.getAppsFlagsMap();

export const adminListApps = (q?: string) => apiService.adminListApps(q);
export const adminCreateApp = (payload: { app_id: string; country_code?: string; label?: string; enabled: boolean }) => apiService.adminCreateApp(payload);
export const adminUpdateApp = (appId: string, payload: { country_code?: string | null; label?: string | null; enabled?: boolean | null }) => apiService.adminUpdateApp(appId, payload);
export const adminDeleteApp = (appId: string) => apiService.adminDeleteApp(appId);